/**
 * Operate workspace context — selected incident shared across Incident/Fixes/Test/Learning.
 * Incidents exposed to tabs are scoped to the ingested repository.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  OPERATE_TAB_TO_DOMAIN,
  OPERATE_TABS,
  OperateTabId,
  operateTabPath,
  operateContextPath,
  type EngineeringContextRef,
} from '../workspace/projectPhases';
import {
  useOperateIncidentData,
  type OperateIncidentData,
} from './useOperateIncidentData';
import { useProject, useProjectRepositories } from '../../../services/api';
import { filterIncidentsForIngestedRepo, isConnectedInboxEmail } from '../../../lib/repoScopedIncidents';

/** Extra action button hosted beside the Mission selector. */
export type MissionChromeExtraAction = {
  id: string;
  label: string;
  busyLabel?: string;
  tooltip?: string;
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
  /** Visual tone for the outlined control. */
  tone?: 'info' | 'running' | 'critical' | 'neutral';
};

/** Re-run / Refresh / tab actions hosted beside the Mission selector. */
export type MissionChromeActions = {
  lastUpdatedLabel?: string | null;
  onRerun?: () => void;
  onRefresh?: () => void;
  rerunning?: boolean;
  refreshing?: boolean;
  busy?: boolean;
  rerunLabel?: string;
  rerunBusyLabel?: string;
  rerunTooltip?: string;
  /** Additional tab-specific actions (waive, baseline, execute step, …). */
  extraActions?: MissionChromeExtraAction[];
} | null;

interface OperateWorkspaceContextValue extends OperateIncidentData {
  /** All AMS incidents for the project (unfiltered). */
  allIncidents: OperateIncidentData['incidents'];
  activeTab: OperateTabId;
  setIncidentId: (id: string | null) => void;
  goTab: (tab: OperateTabId, incidentId?: string | null) => void;
  /** The Engineering Request's own id when a request-driven mission is active (not its change-request id). */
  requestId: string | null;
  /** Unified selection — Incident today, more mission types later. Null when nothing is selected. */
  engineeringContext: EngineeringContextRef;
  /** Navigate the unified selector to a new context (replaces ad hoc setIncidentId for cross-type switches). */
  goToContext: (ref: EngineeringContextRef) => void;
  /** React Router navigation state (location.state) — use instead of useLocation in lazy chunks. */
  navigationState: unknown;
  missionChromeActions: MissionChromeActions;
  setMissionChromeActions: (
    actions: MissionChromeActions | ((prev: MissionChromeActions) => MissionChromeActions),
  ) => void;
}

const OperateWorkspaceContext = createContext<OperateWorkspaceContextValue | null>(null);

function detectTab(pathname: string): OperateTabId {
  const parts = pathname.split('/');
  const idx = parts.indexOf('operate');
  const seg = idx >= 0 ? parts[idx + 1] : 'incidents';
  if (seg === 'overview' || seg === 'external-changes') return 'incidents';
  if (seg in OPERATE_TAB_TO_DOMAIN) return seg as OperateTabId;
  if (OPERATE_TABS.some((t) => t.id === seg)) return seg as OperateTabId;
  return 'incidents';
}

export const OperateWorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { projectId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab = detectTab(location.pathname);
  const [missionChromeActions, setMissionChromeActionsState] = useState<MissionChromeActions>(null);
  const setMissionChromeActions = useCallback(
    (actions: MissionChromeActions | ((prev: MissionChromeActions) => MissionChromeActions)) => {
      setMissionChromeActionsState((prev) =>
        typeof actions === 'function'
          ? (actions as (p: MissionChromeActions) => MissionChromeActions)(prev)
          : actions,
      );
    },
    [],
  );

  const incidentId = searchParams.get('incident');
  const requestId = searchParams.get('request');
  const engineeringContext: EngineeringContextRef = incidentId
    ? { kind: 'incident', id: incidentId }
    : requestId
      ? { kind: 'request', id: requestId }
      : null;
  const data = useOperateIncidentData(projectId, incidentId);
  const projectQ = useProject(projectId);
  const reposQ = useProjectRepositories(projectId);

  const scopedIncidents = useMemo(
    () =>
      filterIncidentsForIngestedRepo(data.incidents, {
        projectName: projectQ.project?.name,
        repositories: reposQ.data ?? [],
        inboxOnly: true,
      }),
    [data.incidents, projectQ.project?.name, reposQ.data],
  );

  /** Auto-select first scoped incident only on detail tabs that need a selection. */
  useEffect(() => {
    const needsIncident = new Set<OperateTabId>([
      'incident',
      'engineering',
      'fixes',
      'test-environment',
      'learning',
      'executions',
    ]);
    if (!needsIncident.has(activeTab)) return;
    // A request-driven mission (from Requests → Engineering) has no incident
    // to select — don't clobber `?request=` with an auto-selected incident.
    if (requestId) return;
    if (data.incidentsLoading || reposQ.isLoading) return;
    const list = scopedIncidents;
    if (!list.length) return;

    if (incidentId) {
      const stillVisible = list.some((i) => i.id === incidentId);
      if (stillVisible) return;
      // Reported (non-inbox) incidents are opened explicitly from the Reported
      // page — keep them selected instead of snapping to an inbox incident.
      const reported = data.incidents.find((i) => i.id === incidentId);
      if (reported && !isConnectedInboxEmail(reported)) return;
      // Deep-linked incident outside repo scope → snap to a scoped remidiated/first item.
    }

    const preferred =
      list.find(
        (i) =>
          i.resolution === 'operational_remediation_resolved' ||
          i.rca?.some((r) => String(r.type) === 'operational_remediation'),
      ) || list[0];
    if (!preferred?.id || preferred.id === incidentId) return;
    navigate(operateTabPath(projectId, activeTab, preferred.id), { replace: true });
  }, [
    activeTab,
    incidentId,
    requestId,
    scopedIncidents,
    data.incidents,
    data.incidentsLoading,
    reposQ.isLoading,
    navigate,
    projectId,
  ]);

  useEffect(() => {
    // Clear stale Mission-bar actions when leaving tabs that host them.
    const hostsChrome = new Set<OperateTabId>([
      'engineering',
      'fixes',
      'executions',
      'test-environment',
    ]);
    if (!hostsChrome.has(activeTab)) {
      setMissionChromeActionsState(null);
    }
  }, [activeTab]);

  const setIncidentId = useCallback(
    (id: string | null) => {
      navigate(operateTabPath(projectId, activeTab, id), { replace: true });
    },
    [navigate, projectId, activeTab],
  );

  const goTab = useCallback(
    (tab: OperateTabId, forIncident?: string | null) => {
      // Preserve request-scoped missions across Operate tabs (Test → Learning, etc.).
      if (requestId && forIncident === undefined && !incidentId) {
        navigate(operateContextPath(projectId, tab, { kind: 'request', id: requestId }));
        return;
      }
      navigate(operateTabPath(projectId, tab, forIncident !== undefined ? forIncident : incidentId));
    },
    [navigate, projectId, incidentId, requestId],
  );

  const goToContext = useCallback(
    (ref: EngineeringContextRef) => {
      // Always stay on the tab the operator is already viewing. Selecting a
      // request while on Test must open Test for that request — never bounce
      // to Engineering (that bug redirected Test → Engineering).
      navigate(operateContextPath(projectId, activeTab, ref), { replace: true });
    },
    [navigate, projectId, activeTab],
  );

  const value = useMemo(
    () => ({
      ...data,
      projectId,
      allIncidents: data.incidents,
      incidents: scopedIncidents,
      activeTab,
      setIncidentId,
      goTab,
      requestId,
      engineeringContext,
      goToContext,
      navigationState: location.state,
      missionChromeActions,
      setMissionChromeActions,
    }),
    [
      data,
      projectId,
      scopedIncidents,
      activeTab,
      setIncidentId,
      goTab,
      requestId,
      engineeringContext,
      goToContext,
      location.state,
      missionChromeActions,
      setMissionChromeActions,
    ],
  );

  return (
    <OperateWorkspaceContext.Provider value={value}>{children}</OperateWorkspaceContext.Provider>
  );
};

export function useOperateWorkspace(): OperateWorkspaceContextValue {
  const ctx = useContext(OperateWorkspaceContext);
  if (!ctx) {
    throw new Error('useOperateWorkspace must be used within OperateWorkspaceProvider');
  }
  return ctx;
}
